import React, { useEffect, useState, useRef } from 'react';
import { useCookies } from 'react-cookie';
import '../../styles/Dashboard.css';
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";

// Use same API base as Topbar
const API = process.env.REACT_APP_API || 'https://api.industriegaming.fr';

export default function ChatAdmin({ dark, setDark, sidebarOpen, setSidebarOpen, onToggleTheme }) {
    const [convs, setConvs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const socketRef = useRef(null);
    const selectedRef = useRef(null);
    const messagesBodyRef = useRef(null);

    const [cookies] = useCookies(["token"]);

    useEffect(() => {
        let mounted = true;
        fetchConvs();
        (async () => {
            try {
                const mod = await import('socket.io-client');
                const ioClient = mod.io || mod.default || mod;
                if (!mounted) return;
                // force polling transport when websocket upgrade fails (reverse-proxy issues)
                socketRef.current = ioClient(API, { transports: ['polling'], path: '/socket.io', secure: true });
                socketRef.current.emit('identify', { role: 'admin' });
                socketRef.current.on('connect', () => {
                    console.log('admin socket connected', socketRef.current.id);
                    socketRef.current.emit('identify', { role: 'admin' });
                    console.log('admin emitted identify');
                    if (selectedRef.current) {
                        console.log('admin emitting joinConv', selectedRef.current.id);
                        socketRef.current.emit('joinConv', { conversationId: selectedRef.current.id });
                    }
                });
                socketRef.current.on('connect_error', (err) => {
                    console.error('admin socket connect_error', err);
                });
                socketRef.current.on('disconnect', (reason) => {
                    console.log('admin socket disconnected', reason);
                });

                socketRef.current.on('chat:message', ({ conversationId, message }) => {
                    console.log('admin chat:message received', conversationId, message);
                    // append to messages only if currently selected
                    if (selectedRef.current && String(selectedRef.current.id) === String(conversationId)) {
                        setMessages(prev => {
                            if (!message || prev.some(m => String(m.id) === String(message.id))) return prev;
                            return [...prev, message];
                        });
                    }
                    fetchConvs();
                });
                socketRef.current.on('chat:conversation:update', ({ conversation }) => {
                    // update list and if currently selected, update it and messages state
                    fetchConvs();
                    if (selectedRef.current && String(selectedRef.current.id) === String(conversation.id)) {
                        setSelected(conversation);
                        selectedRef.current = conversation;
                        // if closed, clear messages input
                        if (String(conversation.status) === 'closed') {
                            setInput('');
                        }
                    }
                });
                socketRef.current.on('chat:conversation', ({ conversation }) => {
                    // new conversation created by a visitor — refresh list
                    fetchConvs();
                });
                socketRef.current.on('chat:typing', () => {});
            } catch (e) {
                console.error('socket.io-client load error', e);
            }
        })();

        return () => {
            mounted = false;
            if (socketRef.current && socketRef.current.disconnect) socketRef.current.disconnect();
        };
    }, []);

    const fetchConvs = async () => {
        try {
            const headers = {};
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;
            const res = await fetch(`${API}/api/chat/conversations`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setConvs(data);
        } catch (err) {
            console.error('fetchConvs error', err);
        }
    };

    const selectConv = async (conv) => {
        try {
            setSelected(conv);
            selectedRef.current = conv;
            if (socketRef.current) {
                await waitForSocketConnect(socketRef.current);
                socketRef.current.emit('joinConv', { conversationId: conv.id });
            }
            const headers = {};
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;
            const res = await fetch(`${API}/api/chat/${conv.id}/messages`, { headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setMessages(data);
            // mark messages as read for this conversation (admin view)
            try {
                const mres = await fetch(`${API}/api/chat/${conv.id}/read`, { method: 'POST', headers });
                if (mres.ok) {
                    // refresh conversation list to update unread badges
                    fetchConvs();
                }
            } catch (e) {
                console.error('mark read error', e);
            }
        } catch (err) {
            console.error('selectConv error', err);
        }
    };

    const sendMessage = async () => {
        if (!selected || !input.trim()) return;
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;
            const res = await fetch(`${API}/api/chat/${selected.id}/message`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ sender: 'admin', content: input })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await res.json();
            // rely on server socket emission to receive message and append it
            setInput('');
        } catch (err) {
            console.error('sendMessage error', err);
        }
    };

    const handleKeyDownAdmin = (e) => {
        // Enter to send, Shift+Enter for newline (if ever switched to textarea)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const closeConv = async (conv) => {
        if (!conv) return;
        if (!window.confirm('Clore cette conversation ?')) return;
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (cookies.token) headers.Authorization = `Bearer ${cookies.token}`;
            const res = await fetch(`${API}/api/chat/${conv.id}/close`, { method: 'POST', headers });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated = await res.json();
            // refresh list and clear selected
            await fetchConvs();
            setSelected(null);
            selectedRef.current = null;
            setMessages([]);
        } catch (err) {
            console.error('closeConv error', err);
            alert('Erreur lors de la clôture de la conversation.');
        }
    };

    // helper: wait until socket is connected (with timeout)
    function waitForSocketConnect(socket, timeout = 5000) {
        return new Promise((resolve, reject) => {
            if (!socket) return reject(new Error('no socket'));
            if (socket.connected) return resolve();
            const onConnect = () => {
                cleanup();
                resolve();
            };
            const onError = (err) => {
                cleanup();
                reject(err || new Error('connect error'));
            };
            const timer = setTimeout(() => {
                cleanup();
                reject(new Error('connect timeout'));
            }, timeout);
            function cleanup() {
                clearTimeout(timer);
                socket.off('connect', onConnect);
                socket.off('connect_error', onError);
            }
            socket.on('connect', onConnect);
            socket.on('connect_error', onError);
        });
    }

    const scrollToBottom = () => {
        const el = messagesBodyRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            try {
                el.scrollTop = el.scrollHeight;
            } catch (e) {
                // ignore
            }
        });
    };

    useEffect(() => {
        // whenever messages change or selected conv changes, scroll to bottom if a conv is open
        if (selected) scrollToBottom();
    }, [messages, selected]);

    // token is read from cookies via useCookies

    return (
        <div className={`dashboard ${dark ? "dark" : ""}`}>
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="main">
                <Topbar onToggleSidebar={() => setSidebarOpen(true)} onToggleTheme={onToggleTheme} dark={dark} />

                <div className="content">
                <div style={{ display:'flex', gap:12 }}>
                    <div style={{ width:240, borderRight:'1px solid #eee', padding:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <h3 style={{ margin:0 }}>Conversations</h3>
                    <button className="chat-btn chat-btn-ghost" onClick={fetchConvs}>Refresh</button>
                </div>
                {convs.length === 0 && <div style={{ padding:8, color:'#666' }}>Aucune conversation</div>}
                {convs.map(c => (
                    <div key={c.id} style={{ padding:8, cursor:'pointer', borderBottom:'1px solid #f0f0f0', display:'flex', justifyContent:'space-between', alignItems:'center' }} onClick={() => selectConv(c)}>
                        <div>
                            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                                <div>{c.title || 'Visitor'}</div>
                                {String(c.status) === 'closed' && <span style={{ fontSize:12, color:'#b91c1c', background:'#fff1f2', padding:'2px 6px', borderRadius:6 }}>Clôturée</span>}
                            </div>
                            <small style={{ color: String(c.status) === 'closed' ? '#888' : '#444' }}>{new Date(c.updatedAt).toLocaleString()}</small>
                        </div>
                        <div style={{ marginLeft:8, display:'flex', alignItems:'center', gap:8 }}>
                            {c.unreadCount > 0 && (
                                <span style={{ fontSize:12, background:'#007bff', color:'#fff', padding:'4px 8px', borderRadius:12 }}>{c.unreadCount}</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
                <div style={{ padding:8, borderBottom:'1px solid #eee', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <h3 style={{ margin:0 }}>{selected ? selected.title : 'Sélectionnez une conversation'}</h3>
                    {selected && (
                        <div style={{ display:'flex', gap:8 }}>
                            <button className="chat-btn chat-btn-ghost" onClick={fetchConvs}>Refresh</button>
                            {String(selected.status) !== 'closed' && (
                                <button className="chat-btn chat-btn-danger" onClick={() => closeConv(selected)}>Clôturer</button>
                            )}
                        </div>
                    )}
                </div>
                <div ref={messagesBodyRef} style={{ flex:1, padding:8, overflow:'auto' }}>
                    {messages.map(m => (
                        <div key={m.id} style={{ margin:6, textAlign: m.sender === 'admin' ? 'right' : 'left' }}>
                            <div style={{ display:'inline-block', padding:8, borderRadius:8, background: m.sender === 'admin' ? '#007bff' : '#eee', color: m.sender === 'admin' ? 'white' : '#000' }}>{m.content}</div>
                        </div>
                    ))}
                </div>
                {selected && (
                    <div className="chat-admin-input-row">
                        <input className="chat-admin-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKeyDownAdmin} disabled={selected && String(selected.status) === 'closed'} placeholder={selected && String(selected.status) === 'closed' ? 'Conversation clôturée' : ''} />
                        <button className="chat-admin-btn" onClick={sendMessage} disabled={selected && String(selected.status) === 'closed'}>Répondre</button>
                    </div>
                )}
                </div>
            </div>
        </div>
        </div>
    </div>
    );
}
